import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Header from '../components/ui/Header';
import AuthenticationGuard from '../components/ui/AuthenticationGuard';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import ThemeToggle from '../components/ui/ThemeToggle';
import SmartLoadingBuffer from '../components/ui/SmartLoadingBuffer';
import useAuth from '../hooks/useAuth';
import { fetchHabitsWithLogs } from '../services/habitsService';

const rangeToDays = {
  week: 7,
  month: 30,
  year: 365
};

const ProgressPage = () => {
  const { user, initializing } = useAuth();
  const [habits, setHabits] = useState([]);
  const [habitLogs, setHabitLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('week');

  const todayISO = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString().slice(0, 10);
  }, []);

  const loadProgress = useCallback(async () => {
    if (!user?.id) {
      setHabits([]);
      setHabitLogs([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { habits: fetchedHabits, logs } = await fetchHabitsWithLogs(user.id);
      setHabits(fetchedHabits ?? []);
      setHabitLogs(logs ?? []);
    } catch (err) {
      console.error('Error loading progress:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!initializing) {
      loadProgress();
    }
  }, [initializing, loadProgress]);

  const completedLogs = useMemo(() => habitLogs.filter((log) => log?.status === 'completed'), [habitLogs]);

  const logsByHabit = useMemo(() => {
    return completedLogs.reduce((acc, log) => {
      if (!acc[log?.habit_id]) {
        acc[log?.habit_id] = [];
      }
      acc[log?.habit_id].push(log);
      return acc;
    }, {});
  }, [completedLogs]);

  const uniqueCompletedDates = useMemo(() => {
    return new Set(completedLogs.map((log) => log?.logged_on));
  }, [completedLogs]);

  const calculateHabitStreak = useCallback((habitId) => {
    const logs = logsByHabit?.[habitId];
    if (!logs?.length) {
      return 0;
    }

    const dates = new Set(logs.map((log) => log?.logged_on));
    let streak = 0;
    const cursor = new Date();
    cursor.setHours(0, 0, 0, 0);

    while (true) {
      const iso = cursor.toISOString().slice(0, 10);
      if (dates.has(iso)) {
        streak += 1;
        cursor.setDate(cursor.getDate() - 1);
      } else {
        break;
      }
    }

    return streak;
  }, [logsByHabit]);

  const currentStreak = useMemo(() => {
    if (!uniqueCompletedDates.size) {
      return 0;
    }

    let streak = 0;
    const cursor = new Date();
    cursor.setHours(0, 0, 0, 0);

    while (true) {
      const iso = cursor.toISOString().slice(0, 10);
      if (uniqueCompletedDates.has(iso)) {
        streak += 1;
        cursor.setDate(cursor.getDate() - 1);
      } else {
        break;
      }
    }

    return streak;
  }, [uniqueCompletedDates]);

  const bestStreak = useMemo(() => {
    if (!uniqueCompletedDates.size) {
      return 0;
    }

    const sortedDates = Array.from(uniqueCompletedDates).sort();
    let best = 0;
    let current = 0;
    let previous = null;

    sortedDates.forEach((iso) => {
      if (!previous) {
        current = 1;
        previous = iso;
        best = Math.max(best, current);
        return;
      }

      const nextExpected = new Date(previous);
      nextExpected.setDate(nextExpected.getDate() + 1);
      const expectedIso = nextExpected.toISOString().slice(0, 10);

      if (iso === expectedIso) {
        current += 1;
      } else {
        current = 1;
      }

      previous = iso;
      best = Math.max(best, current);
    });

    return best;
  }, [uniqueCompletedDates]);

  const totalHabits = habits?.length ?? 0;

  const completedToday = useMemo(() => {
    return completedLogs.filter((log) => log?.logged_on === todayISO).length;
  }, [completedLogs, todayISO]);

  const timeRangeDays = rangeToDays[timeRange] ?? rangeToDays.week;

  const rangeStartDate = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (timeRangeDays - 1));
    return start;
  }, [timeRangeDays]);

  const logsWithinRange = useMemo(() => {
    return completedLogs.filter((log) => {
      const logDate = new Date(log?.logged_on);
      logDate.setHours(0, 0, 0, 0);
      return logDate >= rangeStartDate;
    });
  }, [completedLogs, rangeStartDate]);

  const completionPercentage = useMemo(() => {
    const possible = totalHabits * timeRangeDays;
    if (!possible) {
      return 0;
    }
    return Math.round((logsWithinRange.length / possible) * 100);
  }, [logsWithinRange.length, timeRangeDays, totalHabits]);

  const progressSeries = useMemo(() => {
    if (!totalHabits) {
      return [];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const series = [];

    if (timeRange === 'week') {
      for (let offset = 6; offset >= 0; offset -= 1) {
        const day = new Date(today);
        day.setDate(today.getDate() - offset);
        const iso = day.toISOString().slice(0, 10);
        const completed = completedLogs.filter((log) => log?.logged_on === iso).length;
        series.push({
          label: day.toLocaleDateString(undefined, { weekday: 'short' }),
          completed,
          total: totalHabits
        });
      }
      return series;
    }

    if (timeRange === 'month') {
      const buckets = 4;
      for (let i = buckets - 1; i >= 0; i -= 1) {
        const start = new Date(today);
        start.setDate(today.getDate() - (i * 7 + 6));
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);

        const completed = completedLogs.filter((log) => {
          const logDate = new Date(log?.logged_on);
          return logDate >= start && logDate <= end;
        }).length;

        series.push({
          label: `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
          completed,
          total: totalHabits * 7
        });
      }
      return series;
    }

    for (let i = 11; i >= 0; i -= 1) {
      const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const nextMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1);

      const completed = completedLogs.filter((log) => {
        const logDate = new Date(log?.logged_on);
        return logDate >= monthDate && logDate < nextMonth;
      }).length;

      const daysInMonth = Math.round((nextMonth - monthDate) / (1000 * 60 * 60 * 24));

      series.push({
        label: monthDate.toLocaleDateString(undefined, { month: 'short' }),
        completed,
        total: totalHabits * daysInMonth
      });
    }

    return series;
  }, [completedLogs, timeRange, totalHabits]);

  const habitPerformance = useMemo(() => {
    if (!habits?.length) {
      return [];
    }

    const start = new Date(rangeStartDate);

    return habits.map((habit) => {
      const logs = (logsByHabit?.[habit?.id] ?? []).filter((log) => {
        const logDate = new Date(log?.logged_on);
        return logDate >= start;
      });

      const targetPerWeek = habit?.target_per_week ?? null;
      const expected = targetPerWeek ? Math.max(targetPerWeek * (timeRangeDays / 7), targetPerWeek) : timeRangeDays;
      const completion = expected ? Math.min(100, Math.round((logs.length / expected) * 100)) : 0;

      return {
        name: habit?.name ?? 'Untitled habit',
        category: habit?.category ?? 'General',
        completion,
        streak: calculateHabitStreak(habit?.id)
      };
    });
  }, [habits, logsByHabit, rangeStartDate, timeRangeDays, calculateHabitStreak]);

  const achievements = useMemo(() => {
    if (!habits?.length) {
      return [];
    }

    const achievementsList = [];
    const completedCount = logsWithinRange.length;

    achievementsList.push({
      name: 'Keep the chain',
      description: 'Log at least one habit every day for three days straight.',
      unlocked: currentStreak >= 3,
      progress: Math.min(100, Math.round((currentStreak / 3) * 100)),
      date: currentStreak >= 3 ? todayISO : null
    });

    achievementsList.push({
      name: 'Momentum builder',
      description: 'Reach a best streak of 10 consecutive days.',
      unlocked: bestStreak >= 10,
      progress: Math.min(100, Math.round((bestStreak / 10) * 100)),
      date: bestStreak >= 10 ? todayISO : null
    });

    achievementsList.push({
      name: 'Consistent learner',
      description: `Complete ${timeRangeDays} habit actions in the selected range.`,
      unlocked: completedCount >= timeRangeDays,
      progress: Math.min(100, Math.round((completedCount / timeRangeDays) * 100)),
      date: completedCount >= timeRangeDays ? todayISO : null
    });

    return achievementsList;
  }, [habits, logsWithinRange.length, currentStreak, bestStreak, timeRangeDays, todayISO]);

  if (isLoading || initializing) {
    return (
      <AuthenticationGuard>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="pt-16">
            <SmartLoadingBuffer
              className="min-h-[60vh]"
              title="Crunching your progress signals"
              message="Were compiling habit logs, streaks, and completion trends to keep your momentum on track."
              hint="Cogito is translating your effort into insights."
            />
          </div>
        </div>
      </AuthenticationGuard>
    );
  }

  return (
    <AuthenticationGuard isAuthenticated={true}>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">Progress Tracking</h1>
                  <p className="text-muted-foreground">Monitor your learning journey and achievements</p>
                </div>
                <div className="flex items-center space-x-2">
                  {['week', 'month', 'year']?.map((range) => (
                    <button
                      key={range}
                      onClick={() => setTimeRange(range)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                        timeRange === range
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
              </div>
              <div className="flex items-center space-x-2">
                {['week', 'month', 'year']?.map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                      timeRange === range
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} className="text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {completedToday}/{totalHabits}
                  </p>
                  <p className="text-sm text-muted-foreground">Today's progress</p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Flame" size={24} className="text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{currentStreak}</p>
                  <p className="text-sm text-muted-foreground">Current streak (days)</p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} className="text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{completionPercentage}%</p>
                  <p className="text-sm text-muted-foreground">Completion ({timeRange})</p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Award" size={24} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{bestStreak}</p>
                  <p className="text-sm text-muted-foreground">Best streak (days)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Progress Chart */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-6">
                {timeRange === 'week' ? 'Weekly progress' : timeRange === 'month' ? 'Last 4 weeks' : 'Year in review'}
              </h3>
              {progressSeries?.length ? (
                <div className="space-y-4">
                  {progressSeries.map((entry) => (
                    <div key={entry?.label} className="flex items-center space-x-4">
                      <div className="w-20 text-sm font-medium text-muted-foreground">
                        {entry?.label}
                      </div>
                      <div className="flex-1 bg-muted rounded-full h-3 relative">
                        <div
                          className="bg-primary h-3 rounded-full transition-all duration-500"
                          style={{ width: `${entry?.total ? Math.min(100, Math.round((entry?.completed / entry?.total) * 100)) : 0}%` }}
                        ></div>
                      </div>
                      <div className="text-sm font-medium text-foreground">
                        {entry?.completed}/{entry?.total}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No completions logged in this range yet.</p>
              )}
            </div>

            {/* Habit Performance */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h3 className="text-lg font-semibold text-foreground mb-6">Habit performance</h3>
              {habitPerformance?.length ? (
                <div className="space-y-4">
                  {habitPerformance.map((habit) => (
                    <div key={`${habit?.name}-${habit?.category}`} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-foreground">{habit?.name}</p>
                          <p className="text-xs text-muted-foreground">{habit?.category}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-foreground">{habit?.completion}%</p>
                          <p className="text-xs text-muted-foreground">🔥 {habit?.streak} day streak</p>
                        </div>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-500"
                          style={{ width: `${habit?.completion}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Start logging habits to see performance insights.</p>
              )}
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h3 className="text-lg font-semibold text-foreground mb-6">Achievements</h3>
            {achievements?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {achievements.map((achievement) => (
                  <div
                    key={achievement?.name}
                    className={`p-4 rounded-lg border transition-colors ${
                      achievement?.unlocked ? 'bg-success/5 border-success/20' : 'bg-muted/30 border-border'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          achievement?.unlocked ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        <Icon name={achievement?.unlocked ? 'Award' : 'Lock'} size={20} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground mb-1">{achievement?.name}</h4>
                        <p className="text-sm text-muted-foreground mb-2">{achievement?.description}</p>
                        {achievement?.unlocked ? (
                          <p className="text-xs text-success">
                            Unlocked on {achievement?.date ? new Date(achievement?.date).toLocaleDateString() : todayISO}
                          </p>
                        ) : (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">Progress</span>
                              <span className="font-medium">{achievement?.progress}%</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-1.5">
                              <div
                                className="bg-primary h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${achievement?.progress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Log your habits to start unlocking achievements.</p>
            )}
          </div>
        </div>
      </main>
    </div>
  </AuthenticationGuard>
);
};

export default ProgressPage;